/**
 * @module Datepicker
 */

import flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.css';
import 'flatpickr/dist/l10n/ru';
import Field from '../Field';
import { isTouch, lang, toArray, getKeyName } from '../../../scripts/helpers';
import '../../../images/sprite/arrow.svg';
import '../../../images/sprite/calendar.svg';
import Component from '../Component';
import './flatpickr.css';
import './styles.css';

/**
 * Поле выбора даты.
 *
 * Использует плагин flatpickr.
 * Для прямого доступа к функционалу плагина используется поле instance.
 *
 * Поле даты внутри использует Field через вызов его шаблона.
 * Внутри метода init() происходит его инициализация и в дальнейшем он доступен через поле field.
 *
 * @see {@link https://flatpickr.js.org/}
 *
 * @todo Календарь внутри блока с relative некорректно позиционируется, т.к. позиция считается от body.
 * @todo Выбор даты во множественном поле отбрасывает на первый месяц.
 * @todo Значение today некорректно валидируется из-за проверки времени.
 */
export default class Datepicker extends Component {
    static className = 'datepicker';

    static keyCodes = {
        LEFT: 37,
        UP: 38,
        RIGHT: 39,
        DOWN: 40,
    };

    initElements() {
        this.elements = {
            $field: $('.datepicker__field', this.$block),
            $input: $('.field__input', this.$block),
        };
    }

    setTemplateProperties() {
        /**
         * Режим работы дейтпикера.
         * @type {'single' | 'range' | 'multiple'}
         */
        this.mode = this.$block.attr('data-mode') || 'single';

        /**
         * Минимальная дата.
         * @type {string}
         */
        this.min = this.$block.attr('data-min');

        /**
         * Максимальная дата.
         * @type {string}
         */
        this.max = this.$block.attr('data-max');
    }

    setProperties({ defaultDate, disable, enable, inline, hidden = false }) {
        /**
         * Настройки плагина.
         * @type {Object}
         */
        this.options = {
            /* На тачах ручной ввод блокируется, т.к. он вызывает клавиатуру на полэкрана. */
            allowInput: !isTouch(),
            appendTo: this.$block[0],
            ariaDateFormat: 'd.m.Y',
            dateFormat: 'd.m.Y',
            /* По умолчанию плагин на тачах открывает нативный дейтпикер, если функционал календаря позволяет. Т.к. в бланке присутствуют календари со сложной логикой, то для единообразия выставлен true. Если же на проекте используется только базовые календари, то можно рассмотреть вариант с нативным дейтпикером. */
            disableMobile: true,
            inline,
            locale: lang() === 'ru' ? flatpickr.l10ns.ru : flatpickr.l10ns.en,
            maxDate: this.max,
            minDate: this.min,
            mode: this.mode,
            prevArrow: '<svg class="icon icon--arrow"><use xlink:href="#icon-arrow"></use></svg>',
            nextArrow: '<svg class="icon icon--arrow"><use xlink:href="#icon-arrow"></use></svg>',
        };

        if (defaultDate) {
            this.options.defaultDate = toArray(defaultDate);
        }

        if (disable) {
            this.options.disable = toArray(disable);
        }

        if (enable) {
            this.options.enable = toArray(enable);
        }

        /**
         * Включает визуальное скрытие инпута.
         * @type {boolean}
         */
        this.hidden = hidden;
    }

    init(options, pluginOptions) {
        super.init(options, pluginOptions);
        this.updateMarkup();
        const field = Field.getInstance(this.elements.$field);
        if (field) {
            this.field = field;
        } else {
            this.field = new Field(this.elements.$field);
            this.field.init();
        }
    }

    initPlugin(pluginOptions) {
        /* this переопределяется в обработчиках, так что сохраняем доступ к нему через self. */
        const self = this;

        Object.assign(this.options, {
            onReady(selectedDates, dateStr, instance) {
                /* Добавляем класс календарю для возможности управления внешней геометрией без нарушения БЭМ */
                $(instance.calendarContainer).addClass('datepicker__calendar');
            },
            onOpen() {
                /* Вешаем класс, блокирующий анимацию label */
                self.elements.$field.addClass('field--calendar-opened');
            },
            onClose() {
                /* Убираем класс, блокирующий анимацию label */
                self.elements.$field.removeClass('field--calendar-opened');

                /* Триггерим расфокус на поле после закрытия календаря */
                self.elements.$input[0].blur();
            },
            onChange() {
                /* Необходимо вручную запускать валидацию поля */
                self.elements.$input.parsley().validate();

                /* И вручную делать активным field */
                self.elements.$field.addClass('field--active');
            },
            ...pluginOptions,
        });

        /**
         * Объект для доступа к функционалу плагина
         * @type {Object}
         */
        this.instance = this.elements.$input.flatpickr(this.options);
    }

    /**
     * Приводит разметку в соответствие параметрам.
     */
    updateMarkup() {
        /* У блоков с начальным значением label должен быть изначально сдвинутым. */
        if (this.options.defaultDate) {
            this.elements.$field.addClass('field--active');
        }

        if (this.hidden) {
            this.elements.$field.addClass('datepicker__field--hidden');
        }
    }
}

$(document).on('input', '.field__input--datepicker', handleInput);
$(document).on('keydown', '.field__input--datepicker', handleKeydown);

function handleInput(e) {
    const { currentTarget } = e;
    const $target = $(currentTarget);
    const $block = $target.closest('.datepicker');
    const datepicker = Datepicker.getInstance($block);
    const { instance } = datepicker;

    /* По умолчанию плагин не проставляет дату в календаре при прямом вводе в поле или при уходе с поля, так что проставляем дату самостоятельно (если она валидна, само собой). */
    if ($target.parsley().isValid()) {
        let date;

        switch (instance.config.mode) {
            case 'single':
                date = $target.val();
                break;
            case 'range':
                date = $target.val().split(' — ');
                break;
            case 'multiple':
                date = $target.val().split(', ');
                break;
            default:
        }

        instance.setDate(date);
    }
}

function handleKeydown(e) {
    const { currentTarget, keyCode } = e;
    const $target = $(currentTarget);
    const key = getKeyName(Datepicker.keyCodes, keyCode);

    if (!key) return;

    /* Нужно блокировать стандартную обработку стрелок плагином, чтобы можно было использовать их в инпуте. По умолчанию плагин перекидывает фокус на календарь и переключает даты. Поэтому мы возвращем фокус на поле и вручную меняем позицию курсора, т.к. плагин блокирует действия по умолчанию. Это единственный вариант без изменения кода самого плагина. К сожалению, Shift в таких полях работать не будет, т.к. повторение дефолтного поведения слишком трудозатратно. */
    $target[0].focus();

    const startPos = $target[0].selectionStart;
    const endPos = $target[0].selectionEnd;
    let newPos;

    if (['LEFT', 'DOWN'].includes(key)) {
        if (startPos === endPos) {
            newPos = startPos - 1 >= 0 ? startPos - 1 : 0;
        } else {
            newPos = startPos;
        }
    } else if (['RIGHT', 'UP'].includes(key)) {
        if (startPos === endPos) {
            newPos = startPos + 1 <= $target.val().length ? startPos + 1 : $target.val().length;
        } else {
            newPos = endPos;
        }
    }

    $target[0].setSelectionRange(newPos, newPos);
}
